import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncoded.append(key, value as string);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d8nfj9a1heuq7aefirk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncoded.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Page Header */}
      <section className="w-full pt-28 md:pt-32 pb-10 md:pb-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-full">
            Contact Us
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight">
            Get in Touch
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
            Have questions about the program, want to partner with us, or need help with your subscription? We would love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
            {/* Form */}
            <div className="w-full lg:w-3/5">
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="first-name" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1.5">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="first-name"
                      name="firstName"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400"
                      placeholder="Your first name"
                    />
                  </div>
                  <div>
                    <label htmlFor="last-name" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1.5">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="last-name"
                      name="lastName"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400"
                      placeholder="Your last name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1.5">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 text-foreground-900 cursor-pointer"
                  >
                    <option value="">Select a topic</option>
                    <option value="general">General Inquiry</option>
                    <option value="subscription">Subscription Help</option>
                    <option value="partnership">Partnership Opportunities</option>
                    <option value="volunteer">Volunteer Group Signup</option>
                    <option value="press">Press & Media</option>
                    <option value="other">Something Else</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400 resize-none"
                    placeholder="Tell us what is on your mind..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                  ></textarea>
                  <p className="mt-1 text-xs text-foreground-500 text-right">
                    {charCount}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full sm:w-auto px-8 py-3 text-sm md:text-base font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>

                {/* Success Message */}
                {formStatus === 'success' && (
                  <div className="p-4 bg-primary-100 border border-primary-200 rounded-md">
                    <p className="text-sm text-primary-700 font-medium flex items-center gap-2">
                      <i className="ri-check-line"></i>
                      Message sent successfully! We will get back to you within 24 hours.
                    </p>
                  </div>
                )}

                {/* Error Message */}
                {formStatus === 'error' && (
                  <div className="p-4 bg-accent-100 border border-accent-200 rounded-md">
                    <p className="text-sm text-accent-700 font-medium flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      Something went wrong. Please try again or email us directly.
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="w-full lg:w-2/5">
              <div className="space-y-6 p-6 md:p-8 bg-background-100 rounded-lg border border-background-200/60">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">
                    Email Us
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600">
                    hello@clearbeaches.org
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">
                    Response Time
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600">
                    We typically respond within 24 hours during business days. For urgent subscription issues, please include your account email.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">
                    Volunteer Group Signup
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600">
                    Are you part of a beach cleanup group that needs kits? Select "Volunteer Group Signup" in the subject dropdown and tell us about your group and which beaches you clean.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">
                    Press Inquiries
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600">
                    For media requests, interviews, or feature stories, select "Press & Media" and we will connect you with the right person.
                  </p>
                </div>

                <div className="pt-4 border-t border-background-200/60">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-3">
                    Follow Our Impact
                  </h3>
                  <div className="flex items-center gap-3">
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="Twitter" rel="nofollow">
                      <i className="ri-twitter-x-line text-sm text-foreground-600"></i>
                    </a>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="Instagram" rel="nofollow">
                      <i className="ri-instagram-line text-sm text-foreground-600"></i>
                    </a>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="Facebook" rel="nofollow">
                      <i className="ri-facebook-line text-sm text-foreground-600"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}